import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useTranslation } from '../i18n';
import { SourceFavicon } from './SourceFavicon';
import { PublicVisibilityBadge } from './PublicVisibilityBadge';

/**
 * Cover-forward package card - the workhorse of Catalog v2.
 */
export interface WorkCardModel {
  publiclyListed?: boolean;
  id: string;
  title: string;
  creator: string;
  creatorClaimed: boolean;
  /**
   * Brand id (e.g. <c>"ko-fi"</c>). Used by SourceFavicon for the
   * brand color / favicon lookup via the static source registry.
   */
  source: string;
  /** User-facing brand name resolved through the source registry. */
  sourceLabel?: string;
  kind: string;
  coverUrl?: string | null;
  tone?: PlateTone;
  likes?: number;
  updatedLabel?: string;
  /**
   * Long-form date tooltip: "Created Apr 9, 2018 · Updated Mar 27, 2025".
   * Set by toCardModel when upstream dates are available; rendered as
   * the title attribute on the date span so hovering surfaces the
   * full timeline without crowding the card.
   */
  updatedTooltip?: string;
  canInstall: boolean;
  noredist?: boolean;
  paywalled?: boolean;
  /**
   * Authenticated viewer's <c>AuthorClaim</c> covers this Work.
   * Server-projected per-user marker (ADR-0015) surfaced by
   * <c>toCardModel</c> as <c>pkg.claimedByMe</c>; drives the accent
   * ownership border. False for anonymous viewers and for authenticated
   * viewers without a covering claim.
   */
  claimedByMe?: boolean;
  /**
   * Lifecycle state surfaced only when the viewer carries the admin
   * role - anonymous and signed-in-non-admin always see <c>undefined</c>.
   * Drives the dashed lifecycle border on the catalog card. The card
   * itself doesn't know about role; <c>toCardModel</c> nulls this field
   * for non-admin viewers so the border treatment is admin-gated by
   * data shape rather than a render-time conditional.
   */
  lifecycleStatus?: 'Suppressed';
}

export type PlateTone =
  | 'amber'
  | 'violet'
  | 'cool'
  | 'rust'
  | 'moss'
  | 'fog'
  | 'rose'
  | 'azure'
  | 'gold';

export interface WorkCardProps {
  pkg: WorkCardModel;
  onOpen?: (pkg: WorkCardModel) => void;
  signedIn?: boolean;
  onLikeChange?: (id: string, liked: boolean) => void;
  liked?: boolean;
  density?: 'compact' | 'roomy';
  class?: string;
}

const PLATE_TONES: Record<PlateTone, string> = {
  amber:  'bg-[radial-gradient(120%_80%_at_70%_30%,#e8a76c_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#a85a3a_0%,transparent_70%),linear-gradient(180deg,#241208_0%,#0a0504_100%)]',
  violet: 'bg-[radial-gradient(120%_80%_at_70%_30%,#7d6da8_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#21183a_0%,transparent_70%),linear-gradient(180deg,#100a1c_0%,#06040c_100%)]',
  cool:   'bg-[radial-gradient(120%_80%_at_70%_30%,#6fa9b6_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#1d2a3a_0%,transparent_70%),linear-gradient(180deg,#0a1118_0%,#07090b_100%)]',
  rust:   'bg-[radial-gradient(120%_80%_at_70%_30%,#c54737_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#3a1a12_0%,transparent_70%),linear-gradient(180deg,#1a0a06_0%,#060302_100%)]',
  moss:   'bg-[radial-gradient(120%_80%_at_70%_30%,#7da888_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#1d3324_0%,transparent_70%),linear-gradient(180deg,#0a1410_0%,#060907_100%)]',
  fog:    'bg-[radial-gradient(120%_80%_at_70%_30%,#a8a39c_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#3a3530_0%,transparent_70%),linear-gradient(180deg,#1a1814_0%,#080706_100%)]',
  rose:   'bg-[radial-gradient(120%_80%_at_70%_30%,#d48097_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#582333_0%,transparent_70%),linear-gradient(180deg,#1c0c12_0%,#080406_100%)]',
  azure:  'bg-[radial-gradient(120%_80%_at_70%_30%,#4a8bb8_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#1a3047_0%,transparent_70%),linear-gradient(180deg,#0a1521_0%,#04080d_100%)]',
  gold:   'bg-[radial-gradient(120%_80%_at_70%_30%,#d4a854_0%,transparent_60%),radial-gradient(100%_70%_at_20%_80%,#5a3d1a_0%,transparent_70%),linear-gradient(180deg,#1c1408_0%,#080603_100%)]',
};

const PLATE_FRAMES: Record<PlateTone, string> = {
  amber:  'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(232,167,108,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(168,90,58,0.40)_0%,transparent_60%)]',
  violet: 'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(125,109,168,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(33,24,58,0.40)_0%,transparent_60%)]',
  cool:   'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(111,169,182,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(29,42,58,0.40)_0%,transparent_60%)]',
  rust:   'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(197,71,55,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(58,26,18,0.40)_0%,transparent_60%)]',
  moss:   'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(125,168,136,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(29,51,36,0.40)_0%,transparent_60%)]',
  fog:    'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(168,163,156,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(58,53,48,0.40)_0%,transparent_60%)]',
  rose:   'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(212,128,151,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(88,35,51,0.40)_0%,transparent_60%)]',
  azure:  'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(74,139,184,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(26,48,71,0.40)_0%,transparent_60%)]',
  gold:   'bg-[radial-gradient(50%_40%_at_95%_5%,rgba(212,168,84,0.45)_0%,transparent_60%),radial-gradient(50%_40%_at_5%_95%,rgba(90,61,26,0.40)_0%,transparent_60%)]',
};

export function WorkCard({
  pkg,
  onOpen,
  signedIn = false,
  onLikeChange,
  liked: likedProp = false,
  density = 'roomy',
  class: extraClass,
}: WorkCardProps): JSX.Element {
  const t = useTranslation();
  const [liked, setLiked] = useState(likedProp);
  const [hover, setHover] = useState(false);

  function handleLike(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (!signedIn) return;
    const next = !liked;
    setLiked(next);
    onLikeChange?.(pkg.id, next);
  }

  const sourceLabel = pkg.sourceLabel ?? pkg.source;
  const tone = pkg.tone ?? 'amber';
  const padding = density === 'compact' ? 'p-3' : 'p-4';
  const titleSize = density === 'compact' ? 'text-base' : 'text-lg';
  const likeCount = (pkg.likes ?? 0) + (liked ? 1 : 0);

  // State-based border treatment per the affordance spec:
  //  • claimed-by-me wins over admin lifecycle (your suppressed work is
  //    a personal concern, not a system warning);
  //  • Suppressed > default;
  //  • hover still ramps the accent-soft glow on top of any treatment.
  // Post ADR-0022 correction (A.6) the only admin-visible non-Published
  // lifecycle is Suppressed - the Discovered branch is dead.
  const borderTreatment =
    pkg.claimedByMe
      ? 'border-2 border-accent shadow-[0_0_12px_-4px_var(--accent,#d4a854)]'
      : pkg.lifecycleStatus === 'Suppressed'
        ? 'border border-dashed border-state-failed'
        : `border ${hover ? 'border-accent-soft' : 'border-rule'}`;

  return (
    <article
      class={`group relative flex cursor-pointer flex-col overflow-hidden rounded-md bg-surface-raised transition-transform duration-150 ${hover ? '-translate-y-0.5' : ''} ${borderTreatment} ${extraClass ?? ''}`}
      onClick={() => onOpen?.(pkg)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={t('card.ariaOpen', { title: pkg.title })}
    >
      <div class={`relative aspect-[4/5] overflow-hidden ${pkg.coverUrl ? 'bg-surface-base' : PLATE_TONES[tone]}`}>
        {pkg.coverUrl && (
          <>
            <img
              src={pkg.coverUrl}
              alt=""
              class="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div class={`pointer-events-none absolute inset-0 ${PLATE_FRAMES[tone]}`} />
          </>
        )}
        <div class="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-surface-base/90 to-transparent" />

        <div class="absolute right-2 top-2 flex flex-col items-end gap-1">
          {pkg.noredist && (
            <Badge tone="lock">{t('card.badgeNoredist')}</Badge>
          )}
          {pkg.paywalled && (
            <Badge tone="lock">{t('card.badgePaywall')}</Badge>
          )}
        </div>

        <div class="absolute left-2 top-2">
          <span class="inline-flex items-center gap-1.5 rounded-full bg-surface-base/80 px-2 py-1 text-[10.5px] text-fg-primary backdrop-blur">
            <SourceFavicon source={pkg.source} size={10} />
            {sourceLabel}
          </span>
        </div>

        <div class="absolute bottom-2 left-2">
          <span class="rounded-full bg-surface-base/80 px-2 py-0.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-fg-primary backdrop-blur">
            {pkg.kind}
          </span>
        </div>

        <button
          type="button"
          onClick={handleLike}
          aria-label={t(liked ? 'card.ariaUnlike' : 'card.ariaLike')}
          disabled={!signedIn}
          class={`absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-surface-base/80 px-2 py-1 font-mono text-[11px] backdrop-blur transition-colors ${liked ? 'text-cool' : 'text-fg-muted hover:text-fg-primary'} ${!signedIn ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <HeartIcon filled={liked} />
          {likeCount}
        </button>
      </div>

      <div class={`flex flex-1 flex-col gap-1.5 ${padding}`}>
        <h3 class={`font-display tracking-tight text-fg-primary ${titleSize} leading-tight`}>
          {pkg.title}
        </h3>
        <PublicVisibilityBadge publiclyListed={pkg.publiclyListed} />
        <div class="flex items-center gap-1.5 text-[12px] text-fg-muted">
          <span>{t('card.bylinePrefix')}</span>
          <span
            class={`border-b border-dotted ${pkg.creatorClaimed ? 'text-fg-primary not-italic border-fg-muted' : 'italic text-fg-muted border-fg-faint'}`}
            title={pkg.creatorClaimed ? undefined : t('card.unclaimedSuffix')}
            aria-label={pkg.creatorClaimed ? pkg.creator : `${pkg.creator} (${t('card.unclaimedSuffix')})`}
            onClick={(e) => e.stopPropagation()}
          >
            {pkg.creator}
          </span>
        </div>

        <div class="mt-1.5 flex items-center justify-between border-t border-rule pt-2 font-mono text-[10.5px] uppercase tracking-[0.04em] text-fg-faint">
          <span title={pkg.updatedTooltip}>{pkg.updatedLabel ?? ''}</span>
          <span>{pkg.canInstall ? t('card.actionInstall') : t('card.actionVisit')}</span>
        </div>
      </div>
    </article>
  );
}

function Badge({ children, tone }: { children: JSX.Element | string; tone: 'archive' | 'lock' }): JSX.Element {
  const cls =
    tone === 'archive'
      ? 'border-[#d49254]/45 bg-[#d49254]/10 text-[#d49254]'
      : 'border-state-stale/45 bg-state-stale/10 text-state-stale';
  return (
    <span class={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${cls}`}>
      {children}
    </span>
  );
}

function HeartIcon({ filled }: { filled: boolean }): JSX.Element {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}
